import React, { useState, useEffect, Fragment } from "react";
import firebase from "../firebase.js";
import { checkCommentSafety } from "../utils/profanityCheck";
import {
    Map,
    TileLayer,
    LayersControl,
    Marker,
    GeoJSON,
    Popup,
} from "react-leaflet";
import Search from "react-leaflet-search";
import MarkerClusterGroup from "react-leaflet-markercluster";
import L from "leaflet";
import moment from "moment";

import {
    getIcons,
    getIconByCategory,
    getBoundary,
    getRelationshipText,
    generateSampleData,
} from "../helpers";
import Draw from "./Draw";
import Sidepanel from "./Sidepanel";
import { SURVEY_OPEN } from "../config";
import PopupComment from "./PopupComment";
import "./Leaflet.css";

function Leaflet(props) {
    const user = props.user;
    const admin = props.admin;
    // const boundary = getBoundary();
    const icons = getIcons();
    const mapStyle = { height: "100vh" };
    
    // Responsive map center: Desktop adjusted left, Mobile original
    const getMapCenter = () => {
        const isDesktop = window.innerWidth >= 1024;
        if (isDesktop) {
            return [38.9696, -77.41]; // Desktop: moved left (less right)
        } else {
            return [38.9696, -77.3892]; // Mobile: original position
        }
    };
    
    const [position, setPosition] = useState(getMapCenter());
    const [map, setMap] = useState({});
    const [points, setPoints] = useState({});
    const [comments, setComments] = useState({});
    const [showOldComments, setShowOldComments] = useState(false);
    const [replies, setReplies] = useState({});
    const [openPopupId, setOpenPopupId] = useState(null);
    const [userCreatedComments, setUserCreatedComments] = useState(new Set());

    const saveData = (firebase) => {
        firebase.database().ref("points").set(points);
        firebase.database().ref("comments").set(comments);
        firebase.database().ref("replies").set(replies);
    };

    // Handle window resize to update map center for desktop/mobile
    useEffect(() => {
        const handleResize = () => {
            setPosition(getMapCenter());
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        console.log("Leaflet useEffect fired");

        let pointsRef = null;
        let commentsRef = null;
        let repliesRef = null;

        const attachListeners = () => {
            const db = firebase.database();

            pointsRef = db.ref("points");
            commentsRef = db.ref("comments");
            repliesRef = db.ref("replies");

            pointsRef.on("value", (snapshot) => {
                const pointsData = snapshot.val() || {};
                console.log("=== POINTS LOADED FROM FIREBASE (realtime) ===");
                console.log("Total points:", Object.keys(pointsData).length);
                setPoints(pointsData);
            }, (err) => {
                console.error("ERROR loading points:", err);
            });

            commentsRef.on("value", (snapshot) => {
                const commentsData = snapshot.val() || {};
                console.log("=== COMMENTS LOADED FROM FIREBASE (realtime) ===");
                console.log("Total comments:", Object.keys(commentsData).length);
                setComments(commentsData);
            }, (err) => {
                console.error("ERROR loading comments:", err);
            });

            repliesRef.on("value", (snapshot) => {
                setReplies(snapshot.val() || {});
            }, (err) => {
                console.error("ERROR loading replies:", err);
            });
        };

        if (!user) {
            console.log("No user, signing in anonymously");
            firebase
                .auth()
                .signInAnonymously()
                .then(() => {
                    console.log("Anonymous signin successful");
                    attachListeners();
                })
                .catch(function (error) {
                    console.error("Anonymous signin error:", error.code, error.message);
                });
        } else {
            console.log("User exists, attaching realtime listeners directly");
            attachListeners();
        }

        // Cleanup listeners on unmount
        return () => {
            if (pointsRef) pointsRef.off();
            if (commentsRef) commentsRef.off();
            if (repliesRef) repliesRef.off();
        };
    }, [user]);

    const mapboxSat =
          "https://api.mapbox.com/styles/v1/landaudesign/cmmtejxne001701qt7xtqbaro/tiles/{z}/{x}/{y}?access_token=pk.eyJ1IjoibGFuZGF1ZGVzaWduIiwiYSI6ImNrZ3F4dDIyeTBsMXIyenIzd2EwdnBsZTQifQ.MOBff0ku-Z960ubZPr3b6g";
    const Basemap =
          "https://api.mapbox.com/styles/v1/landaudesign/cmmtejxne001701qt7xtqbaro/tiles/{z}/{x}/{y}?access_token=pk.eyJ1IjoibGFuZGF1ZGVzaWduIiwiYSI6ImNrZ3F4dDIyeTBsMXIyenIzd2EwdnBsZTQifQ.MOBff0ku-Z960ubZPr3b6g";

    delete L.Icon.Default.prototype._getIconUrl;

    L.Icon.Default.mergeOptions({
        iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
        iconUrl: require("leaflet/dist/images/marker-icon.png"),
        shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
    });

    const layerAdd = (e) => {
        const cancelButtons = document.querySelectorAll(".popCancel");
        if (cancelButtons) {
            cancelButtons.forEach(function (button) {
                button.addEventListener("click", onClickCancelButton);
            });
        }

        const saveButtons = document.querySelectorAll(".popSave");
        if (saveButtons) {
            saveButtons.forEach(function (button) {
                button.addEventListener("click", onClickSaveButton);
            });
        }
    };

    const onClickSaveButton = function handler(e) {
        e.target.removeEventListener("click", handler);

        e.target.disabled = true;
        const keys = Object.keys(map.leafletElement._layers);
        const layer =
            map.leafletElement._layers[keys[keys.length - 2]];

        const comment = document.getElementById("commentText")
            .value;
        let name = document.getElementById("commentName").value;
        if (name === "") {
            name = "Anonymous";
        }
        /*const neighboorhood = document.getElementById("commentHood")
            .value;*/
        const live = document.getElementById("popLive").checked;
        const work = document.getElementById("popWork").checked;
        const visit = document.getElementById("popVisit").checked;
        const email = document.getElementById("commentEmail").value;

        layer.properties = {};
        const id = "_" + Math.random().toString(36).substr(2, 9);
        layer.properties.id = id;

        layer.properties.comment = comment;
        layer.properties.name = name;
        layer.properties.email = email;
        /*layer.properties.neighboorhood = neighboorhood;*/
        layer.properties.work = work;
        layer.properties.visit = visit;
        layer.properties.live = live;

        layer.options.id = id;

        map.leafletElement.closePopup();

        let shipText = getRelationshipText(live, work, visit);

        const popup = L.popup({ offset: [0, -20] })
            .setLatLng(layer.getLatLng())
            .setContent(
                `<div class="comment">
                <h3 class="commentContent">` +
                    layer.properties.comment +
                    `</h3>
                <p class="commentBy">
                    by ` +
                    layer.properties.name +
                    `</p>
                <p class="commentDate">` +
                    moment(new Date().toISOString()).fromNow() +
                    `</p>
                <p class="commentAbout">` +
                    shipText +
                    `</p>
                </div>`
            );

        layer.bindPopup(popup);
        layer.openPopup();
        L.zoomSnap = 0;
        let icon = layer.options.icon.options.iconUrl;
        if (icon.includes("issue")) {
            icon = "issue";
        } else if (icon.includes("idea")) {
            icon = "idea";
        } else {
            icon = "place";
        }
        layer.properties.type = icon;

        const coords = layer.getLatLng();
        const pointData = {
            lat: coords.lng,
            lng: coords.lat,
            type: icon,
        };
        if (points) {
            points[layer.properties.id] = pointData;
        }
        //let update = { ...points };
        //setPoints(update);
        const commentData = {
            comment: comment,
            commentBy: name,
            featured: true,
            hidden: false,
            /*neighboorhood: neighboorhood,*/
            work: work,
            live: live,
            visit: visit,
            submittedOn: new Date().toISOString(),
            upvote: 0,
            type: icon,
            email: email,
        };
        
        // Check for profanity/inappropriate content
        const safetyCheck = checkCommentSafety(comment);
        if (safetyCheck.isFlagged) {
            commentData.flagged = true;
            commentData.flagReason = safetyCheck.reason;
            alert(`⚠️ Comment flagged for: ${safetyCheck.reason}`);
        } else {
            commentData.flagged = false;
        }
        if (comments) {
            comments[layer.properties.id] = commentData;
        }
        // let update = { ...comments };
        // setComments(update);

        layer.on("click", () => markerClick(id));

        writePoint(layer.properties.id, pointData, commentData);
    }
    const onClickCancelButton = (e) => {
        map.leafletElement.closePopup();

        const keys = Object.keys(map.leafletElement._layers);
        let layer =
            map.leafletElement._layers[keys[keys.length - 1]];
        map.leafletElement.removeLayer(layer);
    }
    const hideComment = (id) => {
        const toggle = comments[id].hidden;

        points[id].hidden = !toggle;
        let update = { ...points };
        setPoints(update);
        comments[id].hidden = !toggle;
        update = { ...comments };
        setComments(update);

        // write to firebase
        var updates = {};
        updates["/comments/" + id + "/"] = comments[id];
        updates["/points/" + id + "/"] = points[id];
        firebase.database().ref().update(updates);
    };
    const featureComment = (id) => {
        const toggle = comments[id].featured;
        comments[id].featured = !toggle;
        const update = { ...comments };
        setComments(update);

        // write to firebase
        var updates = {};
        updates["/comments/" + id + "/"] = comments[id];
        firebase.database().ref().update(updates);
    };
    const deleteComment = (id) => {
        // Remove from state
        const updatedPoints = { ...points };
        const updatedComments = { ...comments };
        const updatedReplies = { ...replies };
        
        delete updatedPoints[id];
        delete updatedComments[id];
        delete updatedReplies[id];
        
        setPoints(updatedPoints);
        setComments(updatedComments);
        setReplies(updatedReplies);
        
        // Remove from Firebase
        var updates = {};
        updates["/points/" + id] = null;
        updates["/comments/" + id] = null;
        updates["/replies/" + id] = null;
        firebase.database().ref().update(updates);
    };
    const editComment = (id, updatedCommentData) => {
        // Update in state
        comments[id] = {
            ...comments[id],
            ...updatedCommentData,
        };
        const update = { ...comments };
        setComments(update);
        
        // Update in Firebase
        var updates = {};
        updates["/comments/" + id] = comments[id];
        firebase.database().ref().update(updates);
    };
    const hideReply = (id, replyId) => {
        const toggle = replies[id][replyId].hidden;
        replies[id][replyId].hidden = !toggle;
        const update = { ...replies };
        setReplies(update);

        // write to firebase
        var updates = {};
        updates["/replies/" + id + "/" + replyId] = replies[id][replyId];
        firebase.database().ref().update(updates);
    };

    const writePoint = (uid, point, comment) => {
        var updates = {};
        updates["/points/" + uid] = point;
        updates["/comments/" + uid] = comment;
        firebase.database().ref().update(updates);
    };

    const postReply = (e) => {
        // post reply
        const id = e.target.dataset.id;
        const comment = document.getElementById("replyText").value;
        let name = document.getElementById("replyName").value;
        if (name === "") {
            name = "Anonymous";
        }
        const email = document.getElementById("replyEmail").value;
        const live = document.getElementById("replyLive").checked;
        const work = document.getElementById("replyWork").checked;
        const visit = document.getElementById("replyVisit").checked;

        const newReply = {
            comment: comment,
            submittedOn: new Date().toISOString(),
            commentBy: name,
            email: email,
            live: live,
            work: work,
            visit: visit,
            hidden: false,
            upvote: 0,
        };

        //update state
        const replyId = "_" + Math.random().toString(36).substr(2, 9);
        if (!replies[id]) {
            replies[id] = {};
        }
        replies[id][replyId] = newReply;
        const update = { ...replies };
        setReplies(update);

        // write to firebase
        var updates = {};
        updates["/replies/" + id + "/" + replyId] = newReply;
        firebase.database().ref().update(updates);
    };

    const markerClick = (id) => {
        setOpenPopupId(id);
        const sideComment = document.querySelector(`[data-id='${id}']`);
        if (sideComment != null) {
            sideComment.scrollIntoView();
        }
    };

    const unFlagComment = (id) => {
        comments[id].flagged = false;
        comments[id].flagReason = null;
        const update = { ...comments };
        setComments(update);

        // write to firebase
        var updates = {};
        updates["/comments/" + id + "/"] = comments[id];
        firebase.database().ref().update(updates);
    };

    return (
        <Fragment>
            <div id="map-div">
                <Sidepanel 
                    comments={comments} 
                    points={points} 
                    map={map} 
                    admin={admin}
                    toggleHidden={hideComment}
                    unFlagComment={unFlagComment}
                    onToggleOldComments={setShowOldComments}
                />
                <Map
                    ref={(ref) => {
                        setMap(ref);
                    }}
                    className="sidebar-map"
                    id="leaflet-map"
                    center={position}
                    zoom={13.5}
                    style={mapStyle}
                    maxZoom={20}
                    onlayeradd={layerAdd}
                >
                    <Search
                        position="topleft"
                        showMarker={true}
                        showPopup={false}
                        inputPlaceholder="Search for a location"
                        zoom={13} // Default value is 10
                        providerOptions={{ region: "us" }}
                    />
                    <LayersControl position="bottomright">
                        <LayersControl.BaseLayer name="Streets" checked>
                            <TileLayer
                                attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> &copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> map by <a href="https://www.landau.design">LANDAU</a>'
                                url={Basemap}
                            />
                        </LayersControl.BaseLayer>
                        <LayersControl.BaseLayer name="Satellite">
                            <TileLayer
                                attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> &copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> map by <a href="https://www.landau.design">LANDAU</a>'
                                url={mapboxSat}
                            />
                        </LayersControl.BaseLayer>
                        
                    </LayersControl>
                    {SURVEY_OPEN && (
                        <Draw onCommentCreated={(id) => {
                            setUserCreatedComments(prev => new Set([...prev, id]));
                        }} />
                    )}

                    {/* <GeoJSON
                        data={boundary}
                        style={{
                            color: "#f26422",
                            fillColor: "none",
                            weight: 1,
                            // dashArray: "20 10",
                        }}
                    /> */}

                    <MarkerClusterGroup showCoverageOnHover={false} disableClusteringAtZoom={18} >
                        {points &&
                            Object.keys(points).length > 0 &&
                            Object.keys(points).map(
                                (id) =>
                                    !points[id].hidden && comments[id] && !comments[id].hidden && !comments[id].flagged && (showOldComments || comments[id].type === "dreams_2") && (
                                        <Marker
                                            key={id}
                                            id={id}
                                            position={[
                                                points[id].lat,
                                                points[id].lng,
                                            ]}
                                            icon={(() => {
                                                const category = points[id].category || "play";
                                                const type = points[id].type || "dream";
                                                // Normalize dreams_2 to dream for icon file lookup
                                                const normalizedType = type === "dreams_2" ? "dream" : type;
                                                
                                                // For user-created comments, create a new icon instance with the class
                                                if (userCreatedComments.has(id)) {
                                                    // For "general" category, use "pin_" prefix instead of "general_"
                                                    const iconUrl = category === "general"
                                                        ? `/icons/pin_${normalizedType}.svg`
                                                        : `/icons/${category}_${normalizedType}.svg`;
                                                    return L.icon({
                                                        iconUrl: iconUrl,
                                                        iconAnchor: [38, 38],
                                                        iconSize: [76, 76],
                                                        className: `icon-${category} icon-${type} userCreatedMarker`,
                                                        crossOrigin: 'anonymous',
                                                    });
                                                }
                                                
                                                // For other comments, use cached icon
                                                return getIconByCategory(category, type);
                                            })()}
                                            onClick={() => markerClick(id)}
                                        >
                                            {comments[id] && (
                                                <Popup
                                                    id={id}
                                                    featured={
                                                        comments[id].featured
                                                    }
                                                    reply={false}
                                                    open={openPopupId === id}
                                                    onClose={() => setOpenPopupId(null)}
                                                    offset={[0, -20]}
                                                >
                                                    <PopupComment
                                                        admin={admin}
                                                        comment={comments && comments[id]}
                                                        point={points[id]}
                                                        replies={replies && replies[id]}
                                                        id={id}
                                                        postReply={postReply}
                                                        hideComment={
                                                            hideComment
                                                        }
                                                        featureComment={
                                                            featureComment
                                                        }
                                                        deleteComment={
                                                            deleteComment
                                                        }
                                                        editComment={
                                                            editComment
                                                        }
                                                        hideReply={hideReply}
                                                        isUserComment={userCreatedComments.has(id)}
                                                    />
                                                </Popup>
                                            )}
                                        </Marker>
                                    )
                            )}
                    </MarkerClusterGroup>
                </Map>
            </div>
        </Fragment>
    );
}

export default Leaflet;
